import { startCase, camelCase, snakeCase } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Checkbox, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { tableCase } from '@sierralabs/core';
import { api, AppSnackbar, ConfigFieldControl } from '@sierralabs/core-ui';
import { useCreateEntity } from '../hooks/useCreateEntity';
import TableTypePicker from '../pickers/TableTypePicker';
import { useEntityContext } from '../EntityContext';
const StyledAlertTitle = styled(AlertTitle) `
  font-size: 15px;
  font-weight: 600;
`;
const EditEntityDialog = ({ open, entity, onCancel, onEdited }) => {
    const [formValues, setFormValues] = useState({
        name: entity === null || entity === void 0 ? void 0 : entity.name,
        displayName: entity === null || entity === void 0 ? void 0 : entity.displayName,
        type: (entity === null || entity === void 0 ? void 0 : entity.type) || 'regular',
        versionControlled: (entity === null || entity === void 0 ? void 0 : entity.versionControlled) || false,
        workflowEnabled: (entity === null || entity === void 0 ? void 0 : entity.workflowEnabled) || false
    });
    const { createdEntity, isCreating, error, createEntity } = useCreateEntity();
    const { isQueued, isUpdating, trackChanges, allowVersioning, allowWorkflow } = useEntityContext();
    const onEditedRef = useRef(onEdited);
    onEditedRef.current = onEdited;
    const [warn, setWarn] = useState(false);
    const [confirm, setConfirm] = useState(true);
    const taskConfig = api.getEntity('task', 'Task');
    const handleSubmit = () => {
        if (!confirm || !formValues.name || !formValues.displayName) {
            return;
        }
        formValues.displayName = formValues.displayName.trim();
        if (!entity) {
            createEntity(formValues);
            return;
        }
        entity.name = formValues.name;
        entity.tableName = tableCase(formValues.displayName);
        if (entity.displayName !== formValues.displayName) {
            trackChanges.update('displayName', formValues.displayName);
        }
        if (entity.type !== formValues.type) {
            trackChanges.update('type', formValues.type);
        }
        if (entity.versionControlled !== formValues.versionControlled) {
            trackChanges.update('versionControlled', formValues.versionControlled);
        }
        if (entity.workflowEnabled !== formValues.workflowEnabled) {
            trackChanges.update('workflowEnabled', formValues.workflowEnabled);
        }
        // add/remove version column
        const versionIndex = entity.columns.findIndex(column => {
            const name = column.columnName || snakeCase(column.displayName);
            return name === 'version';
        });
        if (formValues.versionControlled && versionIndex < 0) {
            const column = {
                displayName: 'Version',
                name: 'version',
                type: 'integer',
                locked: true
            };
            trackChanges.insert('columns', column);
        }
        else if (!formValues.versionControlled && versionIndex > -1) {
            trackChanges.delete(`columns[${versionIndex}]`);
        }
        toggleTaskRelationship(formValues.workflowEnabled);
        onEdited === null || onEdited === void 0 ? void 0 : onEdited(entity);
    };
    const toggleTaskRelationship = (workflowEnabled) => {
        if (!entity) {
            return;
        }
        // add/remove taskId column
        const columns = entity.columns || [];
        const columnIndex = columns.findIndex(column => {
            const name = column.columnName || snakeCase(column.displayName);
            return name === 'task_id';
        });
        if (workflowEnabled && columnIndex < 0) {
            const column = {
                displayName: 'Task Id',
                name: 'taskId',
                type: 'integer',
                locked: true
            };
            trackChanges.insert('columns', column);
        }
        else if (!workflowEnabled && columnIndex > -1) {
            trackChanges.delete(`columns[${columnIndex}]`);
        }
        // add name column (used for task name)
        const nameColumnIndex = columns.findIndex(column => {
            const name = column.columnName || snakeCase(column.displayName);
            return name === 'name';
        });
        const nameColumn = columns[nameColumnIndex];
        const nameColumnPath = `columns[${nameColumnIndex}]`;
        if (workflowEnabled) {
            // set `locked: true` to avoid modifying column since it is used by task.name
            if (nameColumnIndex < 0) {
                const column = {
                    displayName: 'Name',
                    name: 'name',
                    type: 'text',
                    locked: true
                };
                trackChanges.insert('columns', column);
            }
            else {
                // update changed properties of existing name column
                const conditions = {
                    locked: !nameColumn.locked,
                    type: nameColumn.type !== 'text',
                    array: nameColumn.array
                };
                // update each to properly run entity migration logic (e.g. alter type)
                if (conditions.locked) {
                    trackChanges.update(`${nameColumnPath}.locked`, true);
                }
                if (conditions.type) {
                    trackChanges.update(`${nameColumnPath}.type`, 'text');
                }
                if (conditions.array) {
                    trackChanges.update(`${nameColumnPath}.array`, false);
                }
                // when updated, change reference to properly re-render EntityFieldListRow
                if (conditions.locked || conditions.type || conditions.array) {
                    trackChanges.update(nameColumnPath, Object.assign({}, trackChanges.get(nameColumnPath)));
                }
            }
        }
        else if (nameColumnIndex > -1 && nameColumn.locked) {
            // unlock if name column exists
            trackChanges.update(`${nameColumnPath}.locked`, false);
        }
        // add/remove task relationship
        const relationIndex = (entity.relations || []).findIndex(relation => camelCase(relation.displayName) === taskConfig.propertyName);
        if (workflowEnabled && relationIndex < 0) {
            const relation = {
                displayName: taskConfig.displayName,
                propertyName: taskConfig.propertyName,
                target: taskConfig.name,
                type: 'one-to-one',
                joinColumn: { name: 'task_id' }
            };
            trackChanges.insert('relations', relation);
        }
        else if (!workflowEnabled && relationIndex > -1) {
            trackChanges.delete(`relations[${relationIndex}]`);
        }
    };
    useEffect(() => {
        var _a;
        if (createdEntity) {
            (_a = onEditedRef.current) === null || _a === void 0 ? void 0 : _a.call(onEditedRef, createdEntity);
        }
    }, [createdEntity]);
    return (React.createElement(Dialog, { fullWidth: true, open: open, onClose: onCancel },
        React.createElement(DialogTitle, null,
            entity ? 'Edit' : 'Create',
            " Entity"),
        React.createElement("form", { onSubmit: event => {
                event.preventDefault();
                handleSubmit();
            } },
            React.createElement(DialogContent, null,
                React.createElement(ConfigFieldControl, null,
                    React.createElement(TextField, { autoFocus: true, fullWidth: true, size: 'small', variant: 'outlined', label: 'Entity Display Name', value: formValues.displayName || '', onChange: event => {
                            const displayName = event.target.value;
                            setFormValues(Object.assign(Object.assign({}, formValues), { displayName, name: startCase(camelCase(displayName)).replace(/ /g, '') }));
                        } })),
                React.createElement(ConfigFieldControl, null,
                    React.createElement(TextField, { autoFocus: true, fullWidth: true, disabled: true, size: 'small', variant: 'outlined', label: 'Entity System Name', value: formValues.name || '', onChange: event => setFormValues(Object.assign(Object.assign({}, formValues), { name: event.target.value })) })),
                React.createElement(ConfigFieldControl, null,
                    React.createElement(TableTypePicker, { disableClearable: true, value: formValues.type, textFieldProps: { label: 'Table Type' }, onChange: type => {
                            setFormValues(formValues => {
                                return Object.assign(Object.assign({}, formValues), { type: type || 'regular' });
                            });
                        } })),
                React.createElement(ConfigFieldControl, null,
                    allowVersioning && (React.createElement(FormControlLabel, { control: React.createElement(Checkbox, { checked: formValues.versionControlled, onChange: () => setFormValues(Object.assign(Object.assign({}, formValues), { versionControlled: !formValues.versionControlled })), color: 'primary' }), label: 'Version Controlled' })),
                    allowWorkflow && (React.createElement(FormControlLabel, { control: React.createElement(Checkbox, { checked: formValues.workflowEnabled, onChange: () => setFormValues(Object.assign(Object.assign({}, formValues), { workflowEnabled: !formValues.workflowEnabled })), color: 'primary' }), label: 'Workflow Enabled' }))),
                React.createElement(EditEntityDialogAlert, { versionControlled: formValues.versionControlled, workflowEnabled: formValues.workflowEnabled, didChange: {
                        versionControlled: !!(entity === null || entity === void 0 ? void 0 : entity.versionControlled) !== formValues.versionControlled,
                        workflowEnabled: !!(entity === null || entity === void 0 ? void 0 : entity.workflowEnabled) !== formValues.workflowEnabled
                    }, onWarn: warn => {
                        setWarn(warn);
                        setConfirm(!warn);
                    } }),
                React.createElement(Collapse, { in: warn, style: { minHeight: 'unset' } },
                    React.createElement(FormControlLabel, { style: { marginLeft: 0 }, label: 'Confirm warning and continue', control: React.createElement(Checkbox, { name: 'confirm', color: 'primary', checked: confirm, onChange: () => setConfirm(!confirm) }) }))),
            React.createElement(DialogActions, null,
                React.createElement(Button, { onClick: onCancel }, "Cancel"),
                React.createElement(Button, { variant: 'contained', color: 'primary', type: 'submit', disabled: !confirm || !formValues.name || !formValues.displayName, endIcon: (isCreating || isQueued || isUpdating) && (React.createElement(CircularProgress, { size: 20 })) }, "Submit"))),
        error && (React.createElement(AppSnackbar, { open: true, severity: 'error' }, error.message || 'An error occurred'))));
};
export default EditEntityDialog;
const EditEntityDialogAlert = props => {
    const { didChange, onWarn } = props;
    const onWarnRef = useRef(onWarn);
    const info = {
        versionControlled: didChange.versionControlled && props.versionControlled,
        workflowEnabled: didChange.workflowEnabled && props.workflowEnabled
    };
    const warn = {
        versionControlled: didChange.versionControlled && !props.versionControlled,
        workflowEnabled: didChange.workflowEnabled && !props.workflowEnabled
    };
    useEffect(() => {
        onWarnRef.current = onWarn;
    }, [onWarn]);
    useEffect(() => {
        var _a;
        (_a = onWarnRef.current) === null || _a === void 0 ? void 0 : _a.call(onWarnRef, warn.versionControlled || warn.workflowEnabled);
    }, [warn.versionControlled, warn.workflowEnabled]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Collapse, { in: info.versionControlled || info.workflowEnabled, style: { minHeight: 'unset' } },
            React.createElement(Alert, { severity: 'info' },
                React.createElement(StyledAlertTitle, null, "Additional Updates"),
                React.createElement(Collapse, { in: info.versionControlled },
                    React.createElement("div", null,
                        "\u2022 Version Controlled: This will add an new column",
                        ' ',
                        React.createElement("strong", null, "version"),
                        " and will create a",
                        ' ',
                        React.createElement("strong", null, "version controlled table"),
                        ".")),
                React.createElement(Collapse, { in: info.workflowEnabled },
                    React.createElement("div", { style: info.versionControlled ? { marginTop: 10 } : undefined },
                        "\u2022 Workflow Enabled: This will add an new column",
                        ' ',
                        React.createElement("strong", null, "taskId"),
                        " and a relationship with the",
                        ' ',
                        React.createElement("strong", null, "Task"),
                        " entity.")))),
        React.createElement(Collapse, { in: warn.versionControlled || warn.workflowEnabled, style: {
                minHeight: 'unset',
                marginTop: info.versionControlled || info.workflowEnabled ? 8 : undefined
            } },
            React.createElement(Alert, { severity: 'warning' },
                React.createElement(StyledAlertTitle, null, "Confirmation Required"),
                React.createElement(Collapse, { in: warn.versionControlled },
                    React.createElement("div", null,
                        "\u2022 Version Controlled Off: This will ",
                        React.createElement("strong", null, "remove"),
                        ' ',
                        "the ",
                        React.createElement("em", null, "version"),
                        " column and will",
                        ' ',
                        React.createElement("strong", null, "permanently delete"),
                        " the",
                        ' ',
                        React.createElement("em", null, "version controlled table"),
                        " and all of its data.")),
                React.createElement(Collapse, { in: warn.workflowEnabled },
                    React.createElement("div", { style: warn.versionControlled ? { marginTop: 10 } : undefined },
                        "\u2022 Workflow Disabled: This will ",
                        React.createElement("strong", null, "remove"),
                        " the",
                        ' ',
                        React.createElement("em", null, "taskId"),
                        " column and the relationship with the ",
                        React.createElement("em", null, "Task"),
                        ' ',
                        "entity."))))));
};
//# sourceMappingURL=EditEntityDialog.js.map